import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppSelector } from '../../store/hooks';

interface DropdownItem {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
}

interface DropdownProps {
  isOpen: boolean;
  onClose: () => void;
  items: DropdownItem[];
  className?: string;
  trigger?: React.ReactNode;
  triggerRef?: React.RefObject<any>;
}

const Dropdown: React.FC<DropdownProps> = ({
  isOpen,
  onClose,
  items,
  className = '',
  trigger,
  triggerRef
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const { theme } = useAppSelector((state) => state.ui);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && triggerRef?.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownWidth = 192; // w-48 = 192px
      const dropdownHeight = items.length * 40 + 8; // Approximate height
      
      let top = rect.bottom + 4; // 4px gap
      let left = rect.right - dropdownWidth; // Align to right edge
      
      // Adjust if dropdown would go off screen
      if (left < 8) {
        left = rect.left;
      }
      if (top + dropdownHeight > window.innerHeight - 8) {
        top = rect.top - dropdownHeight - 4; // Show above instead
      }
      
      setPosition({ top, left });
    }
  }, [isOpen, triggerRef, items.length]);

  if (!isOpen) return null;

  const dropdownContent = (
    <div
      ref={dropdownRef}
      className={`fixed z-50 w-48 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${
        theme === 'dark' 
          ? 'bg-gray-800 border border-gray-700' 
          : 'bg-white border border-gray-200'
      } ${className}`}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
    >
      <div className="py-1">
        {items.map((item, index) => {
          const Icon = item.icon;
          return (
            <button
              key={index}
              onClick={item.onClick}
              disabled={item.disabled}
              className={`w-full text-left px-4 py-2 text-sm flex items-center transition-colors duration-150 ${
                theme === 'dark' 
                  ? 'text-gray-300 hover:bg-gray-700 hover:text-white' 
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              } ${
                item.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
              } ${item.className || ''}`}
            >
              {Icon && <Icon className="h-4 w-4 mr-3" />}
              {item.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  // Use portal to render outside the table container
  return createPortal(dropdownContent, document.body);
};

export default Dropdown;
